import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import bookdocLogo from '../../assets/images/bookdoc-logo.svg';
import appStore from '../../assets/images/corp-dashboard/app_appstore.png';
import googlePlay from '../../assets/images/corp-dashboard/app_googleplay.png';
import appGallery from '../../assets/images/corp-dashboard/app_appgallery.png';
import qrCode from '../../assets/images/corp-dashboard/bd_code.png';
import iconEngagement from '../../assets/images/corp-dashboard/icon-engagement.png';
import iconSteps from '../../assets/images/corp-dashboard/icon-steps.png';
import CorpDashboardApi from '../../services/CorpDashboardApi';
import AnimatedNumber from './AnimatedNumber';
import Donut from './Donut';
import { formatCount, formatStepsLong, formatStepsShort, formatTimestamp, tint } from './format';
import '../../assets/styles/CorporateDashboard.scss';

// The API caches for 5 minutes, so polling faster only re-fetches identical data.
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const CLOCK_INTERVAL_MS = 1000;

const STORE_LINKS = {
    googlePlay: 'https://play.google.com/store/apps/details?id=com.bookdoc.bookdoc&hl=en&pli=1',
    appStore:   'https://apps.apple.com/my/app/bookdoc-go-activ-get-rewards/id1037726744',
    appGallery: 'https://appgallery.huawei.com/app/C102164307'
};

const DEFAULT_THEME = {
    header: '#F97368',
    engagement: '#1B4F91',
    steps: '#00A19A'
};

const PAGE_TITLE = 'Corporate Dashboard - BookDoc';
const PAGE_DESCRIPTION = 'Live BookDoc corporate wellness dashboard: track employee engagement and collective step progress across your workforce.';
const LiveClock = () => {
    const [now, setNow] = useState(() => new Date());

    useEffect(() => {
        const clock = setInterval(() => setNow(new Date()), CLOCK_INTERVAL_MS);
        return () => clearInterval(clock);
    }, []);

    return <span className='corp-dash__timestamp'>{formatTimestamp(now)}</span>;
};

const PageMeta = () => (
    <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name='description' content={PAGE_DESCRIPTION} />
        <meta name='robots' content='noindex, nofollow' />
        <meta property='og:title' content={PAGE_TITLE} />
        <meta property='og:description' content={PAGE_DESCRIPTION} />
    </Helmet>
);

function CorporateDashboard() {
    const navigate = useNavigate();

    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    const load = useCallback(() => {
        return CorpDashboardApi.getDashboard().then(
            (payload) => {
                setData(payload);
                setError('');
                setLoading(false);
            },
            (err) => {
                if (CorpDashboardApi.isUnauthorized(err)) {
                    navigate('/corporate-dashboard/login', { replace: true });
                    return;
                }
                setError(CorpDashboardApi.normaliseError(err));
                setLoading(false);
            }
        );
    }, [navigate]);

    useEffect(() => {
        load();

        const poll = setInterval(load, REFRESH_INTERVAL_MS);

        return () => clearInterval(poll);
    }, [load]);

    // Escape closes the corner menu, which is the only way out once it has been
    // opened by tapping rather than hovering.
    useEffect(() => {
        if (!menuOpen) return undefined;

        const onKeyDown = (event) => {
            if (event.key === 'Escape') setMenuOpen(false);
        };
        window.addEventListener('keydown', onKeyDown);

        return () => window.removeEventListener('keydown', onKeyDown);
    }, [menuOpen]);

    const onRefresh = () => {
        setRefreshing(true);
        load().then(() => setRefreshing(false), () => setRefreshing(false));
    };

    const onLogout = () => {
        setMenuOpen(false);
        // logout() clears the stored token even if the request fails, so the
        // redirect is safe either way.
        CorpDashboardApi.logout().then(() =>
            navigate('/corporate-dashboard/login', { replace: true })
        );
    };

    const employer = (data && data.employer) || {};
    const participation = (data && data.participation) || {};
    const steps = (data && data.steps) || {};
    const challenge = (data && data.challenge) || null;

    const joined = Number(participation.activated_employees) || 0;
    const workforce = Number(participation.total_employees) || 0;
    const notJoined = Math.max(workforce - joined, 0);
    const hasNotJoined = notJoined > 0;
    const engagementRate = hasNotJoined ? Number(participation.rate) || 0 : 100;

    const stepsTarget = challenge ? Number(challenge.target) || 0 : 0;
    const stepsGained = challenge && challenge.progress != null
        ? Number(challenge.progress) || 0
        : Number(steps.total) || 0;
    const stepsLeft = Math.max(stepsTarget - stepsGained, 0);
    const stepsRate = stepsTarget > 0 ? (stepsGained / stepsTarget) * 100 : 0;

    const theme = employer.theme || {};
    const headerColor = theme.header_color || DEFAULT_THEME.header;
    const engagementColor = theme.engagement_color || DEFAULT_THEME.engagement;
    const stepsColor = theme.steps_color || DEFAULT_THEME.steps;
    // Optional server-side: an employer that has not set one falls back to a
    // light wash of its header colour, which is how every sample skin reads.
    const ctaBackgroundColor = theme.cta_background_color || tint(headerColor);

    const themeVars = {
        '--cd-header': headerColor,
        '--cd-engagement': engagementColor,
        '--cd-steps': stepsColor,
        '--cd-cta-bg': ctaBackgroundColor
    };

    const title = (challenge && challenge.name)
        || `${employer.name || 'BookDoc'} Wellness Challenge`;

    if (loading) {
        return (
            <div className='corporate-dashboard corporate-dashboard--message'>
                <PageMeta />
                <p>Loading dashboard&hellip;</p>
            </div>
        );
    }

    if (error && !data) {
        return (
            <div className='corporate-dashboard corporate-dashboard--message'>
                <PageMeta />
                <p className='corp-dash__error'>{error}</p>
                <button type='button' className='corp-dash__retry' onClick={onRefresh}>Try again</button>
            </div>
        );
    }

    return (
        <div className='corporate-dashboard' style={themeVars}>
            <PageMeta />

            {/* Invisible hotspot in the top-right corner. The screen runs
                unattended, so nothing chrome-like is shown until someone
                reaches for it with a pointer, a tap, or the Tab key. */}
            <div
                className={`corp-dash__controls${menuOpen ? ' corp-dash__controls--open' : ''}`}
                onClick={() => setMenuOpen(true)}
                onMouseLeave={() => setMenuOpen(false)}
            >
                <div className='corp-dash__controls-menu'>
                    <button
                        type='button'
                        className='corp-dash__control'
                        onClick={onRefresh}
                        disabled={refreshing}
                    >
                        <i className='fa-solid fa-rotate-right' />
                        <span>{refreshing ? 'Refreshing…' : 'Refresh'}</span>
                    </button>
                    <button type='button' className='corp-dash__control' onClick={onLogout}>
                        <i className='fa-solid fa-right-from-bracket' />
                        <span>Sign out</span>
                    </button>
                </div>
            </div>

            <header className='corp-dash__header'>
                <div className='corp-dash__brand'>
                    <img className='corp-dash__brand-logo' src={bookdocLogo} alt='BookDoc' />
                    {employer.logo_url && (
                        <img
                            className='corp-dash__brand-logo corp-dash__brand-logo--partner'
                            src={employer.logo_url}
                            alt={employer.name || 'Corporate logo'}
                        />
                    )}
                </div>

                <div className='corp-dash__banner'>
                    <h1 className='corp-dash__title'>{title}</h1>

                    <div className='corp-dash__meta'>
                        <div className='corp-dash__meta-heading'>Corporate Dashboard</div>
                        <div className='corp-dash__meta-row'>
                            <LiveClock />
                            <span className='corp-dash__live'>LIVE</span>
                        </div>
                    </div>
                </div>
            </header>

            <main className='corp-dash__body'>
                <section className='corp-dash__card'>
                    <div className='corp-dash__card-head corp-dash__card-head--engagement'>
                        <img className='corp-dash__card-icon' src={iconEngagement} alt='' />
                        <span>Community Engagement</span>
                    </div>
                    <div className='corp-dash__card-body'>
                        <div className='corp-dash__metric'>
                            <AnimatedNumber value={joined} format={formatCount} />
                        </div>
                        <div className='corp-dash__metric-label'>Total Engagement</div>

                        <Donut
                            percent={engagementRate}
                            color={engagementColor}
                            caption='of workforce'
                            approximate={hasNotJoined}
                        />

                        <div className='corp-dash__legend'>
                            <div className='corp-dash__legend-item'>
                                <div className='corp-dash__legend-value'>
                                    <span className='corp-dash__swatch corp-dash__swatch--engagement' />
                                    <AnimatedNumber value={joined} format={formatCount} />
                                </div>
                                <div className='corp-dash__legend-label'>Joined</div>
                            </div>
                            {hasNotJoined && (
                                <div className='corp-dash__legend-item'>
                                    <div className='corp-dash__legend-value'>
                                        <span className='corp-dash__swatch corp-dash__swatch--track' />
                                        <AnimatedNumber value={notJoined} format={formatCount} />
                                    </div>
                                    <div className='corp-dash__legend-label'>Not joined</div>
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                <section className='corp-dash__card'>
                    <div className='corp-dash__card-head corp-dash__card-head--steps'>
                        <img className='corp-dash__card-icon' src={iconSteps} alt='' />
                        <span>Collective Steps Goal</span>
                    </div>
                    <div className='corp-dash__card-body'>
                        <div className='corp-dash__metric'>
                            <AnimatedNumber value={stepsGained} format={formatStepsLong} />
                        </div>
                        <div className='corp-dash__metric-label'>Accumulated Steps</div>

                        <Donut
                            percent={stepsRate}
                            color={stepsColor}
                            caption={stepsTarget > 0 ? `of ${formatStepsShort(stepsTarget)} target` : 'of target'}
                        />

                        <div className='corp-dash__legend'>
                            <div className='corp-dash__legend-item'>
                                <div className='corp-dash__legend-value'>
                                    <span className='corp-dash__swatch corp-dash__swatch--steps' />
                                    <AnimatedNumber value={stepsGained} format={formatStepsShort} />
                                </div>
                                <div className='corp-dash__legend-label'>Steps Gained</div>
                            </div>
                            <div className='corp-dash__legend-item'>
                                <div className='corp-dash__legend-value'>
                                    <span className='corp-dash__swatch corp-dash__swatch--track' />
                                    <AnimatedNumber value={stepsLeft} format={formatStepsShort} />
                                </div>
                                <div className='corp-dash__legend-label'>Steps Left</div>
                            </div>
                        </div>
                    </div>
                </section>

                <aside className='corp-dash__cta'>
                    <h2 className='corp-dash__cta-title'>Activate Corporate<br />Premium Today!</h2>

                    <div className='corp-dash__qr'>
                        <img src={qrCode} alt='Scan to download the BookDoc app' />
                    </div>

                    <div className='corp-dash__stores'>
                        <a href={STORE_LINKS.googlePlay} target='_blank' rel='noreferrer'>
                            <img src={googlePlay} alt='Get it on Google Play' />
                        </a>
                        <a href={STORE_LINKS.appStore} target='_blank' rel='noreferrer'>
                            <img src={appStore} alt='Download on the App Store' />
                        </a>
                        <a href={STORE_LINKS.appGallery} target='_blank' rel='noreferrer'>
                            <img src={appGallery} alt='Explore it on AppGallery' />
                        </a>
                    </div>

                    {employer.activation_code && (
                        <>
                            <p className='corp-dash__cta-label'>Use this activation code:</p>
                            <p className='corp-dash__code'>{employer.activation_code}</p>
                        </>
                    )}
                </aside>
            </main>

            <footer className='corp-dash__footer'>
                <p className='corp-dash__footer-text'>
                    For enquiries or technical support, contact BookDoc at{' '}
                    <a href='tel:1300882362'>1300-88-2362</a> or email{' '}
                    <a href='mailto:care@bookdoc.com'>care@bookdoc.com</a>.
                </p>
                <div className='corp-dash__footer-brand'>
                    <img src={bookdocLogo} alt='BookDoc' />
                    <span>&copy; {new Date().getFullYear()}</span>
                </div>
            </footer>
        </div>
    );
}

export default CorporateDashboard;
